/**
 * Offline-tolerant POS sale queue.
 *
 * Why: cashiers can't pause when the network blips. Each sale is staged
 * in IndexedDB, the receipt is printed regardless, and the queue
 * auto-replays whenever connectivity returns.
 *
 * Public surface:
 *   - enqueueSale(payload)   → Promise<{queued:boolean, posted?:boolean}>
 *   - flushQueue()           → Promise<{posted:number, failed:number}>
 *   - subscribe(listener)    → unsubscribe — listener gets fresh metrics
 *   - getMetrics()           → { pending, lastSyncAt, online }
 */

import { apiClient } from '@kss/api';

const DB_NAME = 'kss-billbook-offline';
const DB_VERSION = 1;
const STORE = 'pos-sales';

interface QueuedSale {
  id?: number;
  endpoint: string;
  payload: unknown;
  queuedAt: string;
  /** Number of failed flush attempts so far. */
  attempts: number;
}

export interface QueueMetrics {
  pending: number;
  lastSyncAt: string | null;
  online: boolean;
}

type Listener = (m: QueueMetrics) => void;

const listeners = new Set<Listener>();
let lastSyncAt: string | null = null;

/* -------------------------------------------------------------------- */
/*  IndexedDB helpers                                                   */
/* -------------------------------------------------------------------- */

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

async function add(sale: QueuedSale): Promise<number> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).add(sale);
    req.onsuccess = () => resolve(req.result as number);
    req.onerror = () => reject(req.error);
  });
}

async function listAll(): Promise<QueuedSale[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as QueuedSale[]);
    req.onerror = () => reject(req.error);
  });
}

async function remove(id: number): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function update(sale: QueuedSale): Promise<void> {
  if (sale.id == null) return;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).put(sale);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function count(): Promise<number> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/* -------------------------------------------------------------------- */
/*  Public API                                                          */
/* -------------------------------------------------------------------- */

/**
 * Try to POST the sale immediately. If the network is down (or the
 * server returns a 5xx that looks transient), stage it in IndexedDB
 * and return queued=true. The caller should print the receipt either
 * way — the cashier never blocks on the network.
 */
export async function enqueueSale(
  endpoint: string,
  payload: unknown,
): Promise<{ queued: boolean; posted: boolean; queueLength: number }> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return staleAndQueue(endpoint, payload);
  }
  try {
    await apiClient.post(endpoint, payload);
    notify();
    return { queued: false, posted: true, queueLength: await safeCount() };
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    // Permanent failure (auth, 4xx) — surface to the caller.
    if (typeof status === 'number' && status >= 400 && status < 500) {
      throw e;
    }
    return staleAndQueue(endpoint, payload);
  }
}

async function staleAndQueue(
  endpoint: string,
  payload: unknown,
): Promise<{ queued: boolean; posted: boolean; queueLength: number }> {
  await add({
    endpoint,
    payload,
    queuedAt: new Date().toISOString(),
    attempts: 0,
  });
  notify();
  return { queued: true, posted: false, queueLength: await safeCount() };
}

/**
 * Replay every queued sale in order. Stops at the first network error
 * to preserve order — pending entries stay queued for the next attempt.
 *
 * Skips entries that have failed 5+ times so a single poison-pill row
 * doesn't block the rest forever.
 */
export async function flushQueue(): Promise<{ posted: number; failed: number }> {
  const sales = await listAll();
  let posted = 0;
  let failed = 0;
  for (const s of sales) {
    if (s.attempts >= 5) {
      failed++;
      continue;
    }
    try {
      await apiClient.post(s.endpoint, s.payload);
      if (s.id != null) await remove(s.id);
      posted++;
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) {
        // Permanent failure — drop the row so it doesn't block forever.
        if (s.id != null) await remove(s.id);
        failed++;
      } else {
        // Likely network — bump attempts and stop, keep order.
        await update({ ...s, attempts: (s.attempts ?? 0) + 1 });
        failed++;
        break;
      }
    }
  }
  if (posted > 0) {
    lastSyncAt = new Date().toISOString();
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'kss-pos-flushed', posted });
    }
  }
  notify();
  return { posted, failed };
}

export async function getMetrics(): Promise<QueueMetrics> {
  return {
    pending: await safeCount(),
    lastSyncAt,
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
  };
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  void getMetrics().then(fn).catch(() => undefined);
  return () => listeners.delete(fn);
}

function notify(): void {
  void getMetrics()
    .then((m) => {
      for (const l of listeners) l(m);
    })
    .catch(() => undefined);
}

async function safeCount(): Promise<number> {
  try { return await count(); } catch { return 0; }
}

/**
 * Wire up window listeners so the queue auto-replays the moment the
 * browser regains connectivity. Call once at app start.
 */
export function initOfflineQueue(): void {
  if (typeof window === 'undefined') return;
  // Replay on initial load (in case we just came back online).
  if (navigator.onLine) {
    void flushQueue().catch(() => undefined);
  }
  window.addEventListener('online', () => {
    notify();
    void flushQueue().catch(() => undefined);
  });
  window.addEventListener('offline', () => notify());
}
